"use client";

import { useEffect, useMemo, useState } from "react";
import { CatalogAutocomplete, type CatalogSuggestion } from "./catalog-autocomplete";
import { NiftyPricePilot } from "./nifty-price-pilot";

type Pharmacy = "Square" | "Ragadan" | "Laurentine";
type Positioning = "aggressive" | "moderate" | "soft" | "high_margin";
type Sensitivity = "very_sensitive" | "sensitive" | "low";
type Catchment = "local" | "mixed" | "destination";

type Props = {
  onBack: () => void;
  initialPharmacy: string;
};

const pharmacies: Pharmacy[] = ["Square", "Ragadan", "Laurentine"];

type CatalogSupplier = {
  supplier: string;
  product_count: number;
  pharmacy_count?: number;
  stock_total: number;
};

type CatalogAnalysisRow = CatalogSuggestion & {
  suggestedPrice: number;
  decidedPrice: number;
  currentMarkRate: number;
  suggestedMarkRate: number;
  decidedMarkRate: number;
  aggressiveness: Positioning;
  action: string;
  aiSensitivity: Sensitivity;
  aiReason: string;
};

const numberValue = (value: unknown) => {
  const parsed = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const money = (value: number) =>
  value.toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });

const roundRetail = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const step = value < 10 ? 0.05 : value < 30 ? 0.1 : 0.5;
  return Math.max(step, Math.round(value / step) * step);
};

const catalogMarkRate = (price: number, purchasePrice: unknown, vatRate: unknown) => {
  const purchase = numberValue(purchasePrice);
  const vat = numberValue(vatRate) || 20;
  const priceExcludingTax = price > 0 ? price / (1 + vat / 100) : 0;
  return priceExcludingTax > 0 && purchase > 0 ? (priceExcludingTax - purchase) / priceExcludingTax * 100 : 0;
};

const aggressivenessOptions: Array<{ value: Positioning; label: string; factor: number }> = [
  { value: "aggressive", label: "Très agressif", factor: 0.96 },
  { value: "moderate", label: "Équilibré", factor: 1 },
  { value: "soft", label: "Valorisation", factor: 1.03 },
  { value: "high_margin", label: "Marge forte", factor: 1.07 },
];

export function PricingAssistantModule({ onBack, initialPharmacy }: Props) {
  const [activeTab, setActiveTab] = useState<"pricing" | "nifty">("pricing");
  const [pharmacy, setPharmacy] = useState<Pharmacy>(
    pharmacies.includes(initialPharmacy as Pharmacy) ? initialPharmacy as Pharmacy : "Square",
  );
  const [lookup, setLookup] = useState("");
  const [product, setProduct] = useState("");
  const [ean, setEan] = useState("");
  const [purchasePrice, setPurchasePrice] = useState(0);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [vatRate, setVatRate] = useState(20);
  const [marketLow, setMarketLow] = useState(0);
  const [marketAverage, setMarketAverage] = useState(0);
  const [marketHigh, setMarketHigh] = useState(0);
  const [marketZone, setMarketZone] = useState(0);
  const [positioning, setPositioning] = useState<Positioning>("moderate");
  const [sensitivity, setSensitivity] = useState<Sensitivity>("sensitive");
  const [sensitivityLoading, setSensitivityLoading] = useState(false);
  const [sensitivityReason, setSensitivityReason] = useState("");
  const [sensitivityConfidence, setSensitivityConfidence] = useState<"high" | "medium" | "low" | "">("");
  const [catchment, setCatchment] = useState<Catchment>("mixed");
  const [simulatedPrice, setSimulatedPrice] = useState(0);
  const [catalogLaboratory, setCatalogLaboratory] = useState("");
  const [catalogScope, setCatalogScope] = useState<"network" | "pharmacy">("network");
  const [catalogSuppliers, setCatalogSuppliers] = useState<CatalogSupplier[]>([]);
  const [catalogFile, setCatalogFile] = useState("");
  const [catalogAnalysis, setCatalogAnalysis] = useState<CatalogAnalysisRow[]>([]);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogSort, setCatalogSort] = useState<"product" | "sensitivity" | "decided_price" | "mark_rate" | "decision">("product");
  const [catalogPhnOnly, setCatalogPhnOnly] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogProgress, setCatalogProgress] = useState(0);
  const [aiConfigured, setAiConfigured] = useState(false);
  const [aiModel, setAiModel] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    void fetch("/api/pricing-assistant", { cache: "no-store" })
      .then((response) => response.json())
      .then((payload: { configured?: boolean; model?: string }) => {
        setAiConfigured(Boolean(payload.configured));
        setAiModel(payload.model ?? "");
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    void fetch("/api/product-catalog?limit=1", { cache: "no-store" })
      .then((response) => response.json())
      .then((payload: { suppliers?: CatalogSupplier[] }) => {
        const suppliers = payload.suppliers ?? [];
        setCatalogSuppliers(suppliers);
        if (!catalogLaboratory && suppliers.length === 1) setCatalogLaboratory(suppliers[0].supplier);
      })
      .catch(() => undefined);
  }, [pharmacy]);

  const analyzeCatalog = async () => {
    if (!catalogLaboratory.trim()) return;
    setCatalogLoading(true);
    setCatalogProgress(0);
    setCatalogAnalysis([]);
    try {
      const pharmacyParameter = catalogScope === "pharmacy" ? `&pharmacy=${encodeURIComponent(pharmacy)}` : "";
      const productTypeParameter = catalogPhnOnly ? `&productTypes=${encodeURIComponent("PHN / conseil")}` : "";
      const response = await fetch(`/api/product-catalog?supplier=${encodeURIComponent(catalogLaboratory)}${pharmacyParameter}${productTypeParameter}&limit=5000`, { cache: "no-store" });
      const payload = await response.json() as { items?: CatalogSuggestion[]; error?: string };
      if (!response.ok) throw new Error(payload.error || "Analyse indisponible.");
      const items = payload.items ?? [];
      if (!items.length) throw new Error("Aucun produit de ce laboratoire dans le catalogue sélectionné.");
      const batches = Array.from({ length: Math.ceil(items.length / 100) }, (_, index) => items.slice(index * 100, index * 100 + 100));
      const aiByEan = new Map<string, { sensitivity: Sensitivity; reason: string }>();
      let completed = 0;
      for (let index = 0; index < batches.length; index += 3) {
        const group = batches.slice(index, index + 3);
        const results = await Promise.all(group.map(async (batch) => {
          const aiResponse = await fetch("/api/pricing-assistant", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mode: "catalog_batch",
              product: catalogLaboratory,
              geography: "Nice / Saint-Laurent-du-Var, trois pharmacies de proximité",
              products: batch.map((item) => ({ ean: item.ean, product: item.product })),
            }),
          });
          const aiPayload = await aiResponse.json() as { analyses?: Array<{ ean?: string; sensitivity?: Sensitivity; reason?: string }>; error?: string };
          if (!aiResponse.ok) throw new Error(aiPayload.error || "Analyse IA du catalogue indisponible.");
          return aiPayload.analyses ?? [];
        }));
        results.flat().forEach((analysis) => {
          if (analysis.ean && ["very_sensitive", "sensitive", "low"].includes(analysis.sensitivity ?? "")) {
            aiByEan.set(analysis.ean, { sensitivity: analysis.sensitivity!, reason: analysis.reason || "Analyse IA du positionnement produit." });
          }
        });
        completed += group.reduce((sum, batch) => sum + batch.length, 0);
        setCatalogProgress(Math.round(completed / items.length * 100));
      }
      const rows = items.map((item) => {
        const purchase = numberValue(item.purchase_price);
        const current = numberValue(item.sale_price);
        const vat = numberValue(item.vat_rate) || 20;
        const market = numberValue(item.market_zone) || numberValue(item.market_average) || current;
        const economic = purchase > 0 ? purchase / (1 - 0.34) * (1 + vat / 100) : current;
        const ai = aiByEan.get(item.ean) ?? { sensitivity: "sensitive" as Sensitivity, reason: "Sensibilité prudente par défaut." };
        const sensitivityFactor = ai.sensitivity === "very_sensitive" ? 0.97 : ai.sensitivity === "low" ? 1.04 : 1;
        const suggestedPrice = roundRetail((economic && market ? economic * 0.72 + market * 0.28 : economic || market) * sensitivityFactor);
        const markRate = (price: number) => {
          const ht = price > 0 ? price / (1 + vat / 100) : 0;
          return ht > 0 && purchase > 0 ? (ht - purchase) / ht * 100 : 0;
        };
        const currentMarkRate = markRate(current);
        const suggestedMarkRate = markRate(suggestedPrice);
        const difference = suggestedPrice - current;
        const action = !purchase || !current ? "Données à compléter" : Math.abs(difference) < 0.11 ? "Prix cohérent" : difference > 0 ? "Hausse à étudier" : "Baisse à étudier";
        return { ...item, suggestedPrice, decidedPrice: suggestedPrice, currentMarkRate, suggestedMarkRate, decidedMarkRate: suggestedMarkRate, aggressiveness: "moderate" as Positioning, action, aiSensitivity: ai.sensitivity, aiReason: ai.reason };
      });
      setCatalogAnalysis(rows);
      setNotice(`${rows.length.toLocaleString("fr-FR")} produit(s) ${catalogPhnOnly ? "PHN " : ""}${catalogLaboratory} analysé(s) ${catalogScope === "network" ? "sur le réseau" : `pour ${pharmacy}`}.`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Analyse catalogue indisponible.");
    } finally {
      setCatalogLoading(false);
    }
  };

  const catalogSummary = useMemo(() => ({
    priced: catalogAnalysis.filter((row) => numberValue(row.purchase_price) > 0 && numberValue(row.sale_price) > 0).length,
    opportunities: catalogAnalysis.filter((row) => row.action.includes("étudier")).length,
    averageMark: catalogAnalysis.length ? catalogAnalysis.reduce((sum, row) => sum + row.currentMarkRate, 0) / Math.max(1, catalogAnalysis.filter((row) => row.currentMarkRate > 0).length) : 0,
  }), [catalogAnalysis]);

  const displayedCatalog = useMemo(() => {
    const sensitivityOrder: Record<Sensitivity, number> = { very_sensitive: 0, sensitive: 1, low: 2 };
    const query = catalogSearch.trim().toLocaleLowerCase("fr");
    return catalogAnalysis
      .filter((row) => !query || `${row.product} ${row.ean}`.toLocaleLowerCase("fr").includes(query))
      .filter((row) => !catalogPhnOnly || String(row.product_type ?? "").toLocaleUpperCase("fr").includes("PHN"))
      .sort((left, right) => {
        if (catalogSort === "sensitivity") return sensitivityOrder[left.aiSensitivity] - sensitivityOrder[right.aiSensitivity] || left.product.localeCompare(right.product, "fr");
        if (catalogSort === "decided_price") return right.decidedPrice - left.decidedPrice;
        if (catalogSort === "mark_rate") return right.decidedMarkRate - left.decidedMarkRate;
        if (catalogSort === "decision") {
          const decisionOrder = (value: string) => value === "Prix cohérent" ? 0 : value === "Baisse à étudier" ? 1 : value === "Hausse à étudier" ? 2 : 3;
          return decisionOrder(left.action) - decisionOrder(right.action) || left.product.localeCompare(right.product, "fr");
        }
        return left.product.localeCompare(right.product, "fr");
      });
  }, [catalogAnalysis, catalogPhnOnly, catalogSearch, catalogSort]);

  const updateCatalogAggressiveness = (row: CatalogAnalysisRow, aggressiveness: Positioning) => {
    const factor = aggressivenessOptions.find((option) => option.value === aggressiveness)?.factor ?? 1;
    const decidedPrice = roundRetail(row.suggestedPrice * factor);
    setCatalogAnalysis((current) => current.map((item) => item.pharmacy === row.pharmacy && item.ean === row.ean
      ? { ...item, aggressiveness, decidedPrice, decidedMarkRate: catalogMarkRate(decidedPrice, item.purchase_price, item.vat_rate) }
      : item));
  };

  const analyzeSensitivity = async (suggestion?: CatalogSuggestion) => {
    const analyzedProduct = suggestion?.product || product.trim();
    if (!analyzedProduct) {
      setNotice("Choisissez ou saisissez d’abord le produit à analyser.");
      return;
    }
    setSensitivityLoading(true);
    setSensitivityReason("");
    setSensitivityConfidence("");
    try {
      const response = await fetch("/api/pricing-assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "sensitivity",
          product: analyzedProduct,
          ean: suggestion?.ean || ean,
          pharmacy: suggestion?.pharmacy || pharmacy,
          purchasePrice: suggestion ? numberValue(suggestion.purchase_price) : purchasePrice,
          currentPrice: suggestion ? numberValue(suggestion.sale_price) : currentPrice,
          vatRate: suggestion ? numberValue(suggestion.vat_rate) || 20 : vatRate,
          geography: "Nice / Saint-Laurent-du-Var, zone urbaine littorale, trois pharmacies de proximité",
        }),
      });
      const payload = await response.json() as {
        sensitivity?: Sensitivity;
        reason?: string;
        signals?: string[];
        confidence?: "high" | "medium" | "low";
        error?: string;
      };
      if (!response.ok || !payload.sensitivity) throw new Error(payload.error || "Analyse indisponible.");
      setSensitivity(payload.sensitivity);
      setSensitivityConfidence(payload.confidence ?? "medium");
      setSensitivityReason(
        [payload.reason, ...(payload.signals ?? []).map((signal) => `• ${signal}`)]
          .filter(Boolean)
          .join("\n") || "Sensibilité estimée automatiquement.",
      );
    } catch (error) {
      setSensitivityReason(error instanceof Error ? `${error.message} Vous pouvez corriger manuellement.` : "Analyse automatique indisponible : choisissez manuellement.");
    } finally {
      setSensitivityLoading(false);
    }
  };

  const applySuggestion = (suggestion: CatalogSuggestion) => {
    setLookup(suggestion.ean);
    setProduct(suggestion.product);
    setEan(suggestion.ean);
    setPurchasePrice(numberValue(suggestion.purchase_price));
    setCurrentPrice(numberValue(suggestion.sale_price));
    setVatRate(numberValue(suggestion.vat_rate) || 20);
    setMarketLow(numberValue(suggestion.market_low));
    setMarketAverage(numberValue(suggestion.market_average));
    setMarketHigh(numberValue(suggestion.market_high));
    setMarketZone(numberValue(suggestion.market_zone));
    setSimulatedPrice(numberValue(suggestion.sale_price));
    setAiAnswer("");
    setSensitivityConfidence("");
    setNotice(`${suggestion.product} reconnu dans le catalogue ${suggestion.pharmacy}.`);
    void analyzeSensitivity(suggestion);
  };

  const recommendation = useMemo(() => {
    const targetMargin = positioning === "aggressive"
      ? 0.27
      : positioning === "high_margin"
        ? 0.46
        : positioning === "soft"
          ? 0.40
          : 0.34;
    const sensitivityFactor = sensitivity === "very_sensitive" ? 0.96 : sensitivity === "low" ? 1.04 : 1;
    const catchmentFactor = catchment === "local" ? 0.98 : catchment === "destination" ? 1.03 : 1;
    const economicTarget = purchasePrice > 0
      ? purchasePrice / (1 - targetMargin) * (1 + vatRate / 100)
      : currentPrice;
    const marketAnchor = marketZone || marketAverage || currentPrice || economicTarget;
    const blended = economicTarget && marketAnchor
      ? economicTarget * 0.68 + marketAnchor * 0.32
      : economicTarget || marketAnchor;
    const target = roundRetail(blended * catchmentFactor * sensitivityFactor);
    const floor = roundRetail(purchasePrice > 0
      ? purchasePrice / (1 - Math.max(0.2, targetMargin - 0.09)) * (1 + vatRate / 100)
      : target * 0.9);
    const ceilingBase = marketHigh || Math.max(target * 1.12, currentPrice);
    const ceiling = roundRetail(Math.max(target, ceilingBase));
    const grossMargin = target > 0 && purchasePrice > 0
      ? ((target / (1 + vatRate / 100) - purchasePrice) / (target / (1 + vatRate / 100))) * 100
      : 0;
    return { floor, target, ceiling, grossMargin, marketAnchor, economicTarget };
  }, [catchment, currentPrice, marketAverage, marketHigh, marketZone, positioning, purchasePrice, sensitivity, vatRate]);

  const margins = useMemo(() => {
    const calculate = (ttc: number) => {
      const ht = ttc > 0 ? ttc / (1 + vatRate / 100) : 0;
      const euros = ht > 0 && purchasePrice > 0 ? ht - purchasePrice : 0;
      const marginRate = purchasePrice > 0 && ht > 0 ? euros / purchasePrice * 100 : 0;
      const markRate = ht > 0 && purchasePrice > 0 ? euros / ht * 100 : 0;
      return { ht, euros, marginRate, markRate };
    };
    return {
      current: calculate(currentPrice),
      simulation: calculate(simulatedPrice || recommendation.target),
    };
  }, [currentPrice, purchasePrice, recommendation.target, simulatedPrice, vatRate]);

  const askAi = async () => {
    if (!product.trim() || recommendation.target <= 0) {
      setNotice("Choisissez un produit et renseignez au moins un prix d’achat, de vente ou de marché.");
      return;
    }
    setAiLoading(true);
    setAiAnswer("");
    try {
      const response = await fetch("/api/pricing-assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product,
          ean,
          pharmacy,
          purchasePrice,
          currentPrice,
          vatRate,
          marketLow,
          marketAverage,
          marketHigh,
          marketZone,
          positioning,
          sensitivity,
          catchment,
          simulatedPrice: simulatedPrice || recommendation.target,
          currentMarkRate: margins.current.markRate,
          simulatedMarkRate: margins.simulation.markRate,
          localRecommendation: recommendation,
          geography: "Saint-Laurent-du-Var, zone Nice Ouest et littoral des Alpes-Maritimes",
        }),
      });
      const payload = await response.json() as { answer?: string; error?: string };
      if (!response.ok) throw new Error(payload.error || "Analyse indisponible.");
      setAiAnswer(payload.answer ?? "");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Analyse indisponible.");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <main className="pricing-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark">
          <span className="wordmark-symbol"><i /><i /><i /></span>
          <span><strong>saint-laurent</strong><small>pharmacies</small></span>
        </div>
        <div className="module-header-actions">
          <a className="module-tools-shortcut" href="/?tools=1"><span>🧰</span><b>Applications</b></a>
          <span className="module-status"><i /> Simulateur confidentiel</span>
        </div>
      </header>

      <section className="pricing-page">
        <header className="pricing-hero">
          <div>
          <span>✨ PRIX · TAUX DE MARQUE · ZONE DE CHALANDISE</span>
            <h1>Aide à la fixation des prix</h1>
            <p>Une recommandation chiffrée, expliquée et ajustable. Le prix final reste toujours validé par le titulaire.</p>
          </div>
          <aside>
            <small>ZONE ANALYSÉE</small>
            <strong>Saint-Laurent-du-Var</strong>
            <span>Nice Ouest · littoral · clientèle locale et de passage</span>
          </aside>
        </header>

        <nav className="pricing-module-tabs" aria-label="Rubriques du module prix">
          <button type="button" className={activeTab === "pricing" ? "active" : ""} aria-current={activeTab === "pricing" ? "page" : undefined} onClick={() => setActiveTab("pricing")}><span>€</span><div><strong>Conseil prix</strong><small>Prix unitaire et analyse catalogue laboratoire</small></div></button>
          <button type="button" className={activeTab === "nifty" ? "active nifty" : "nifty"} aria-current={activeTab === "nifty" ? "page" : undefined} onClick={() => setActiveTab("nifty")}><span>★</span><div><strong>Nifty</strong><small>Promotions, marge et retour au prix normal</small></div></button>
        </nav>

        {activeTab === "nifty" ? <NiftyPricePilot pharmacy={pharmacy} onPharmacyChange={setPharmacy} /> : <>
        <section className="pricing-workspace">
          <div className="pricing-inputs">
            <header><span>01</span><div><h2>Produit et données disponibles</h2><p>Scannez un EAN, tapez un nom ou utilisez <b>*1234</b>.</p></div></header>
            <div className="pricing-form-grid">
              <label className="wide"><span>Pharmacie</span><div className="pricing-pharmacies">{pharmacies.map((item) => <button type="button" key={item} className={pharmacy === item ? "active" : ""} onClick={() => setPharmacy(item)}>{pharmacy === item ? "✓ " : ""}{item}</button>)}</div></label>
              <label className="wide"><span>EAN ou dénomination</span><CatalogAutocomplete value={lookup} onChange={setLookup} onPick={applySuggestion} placeholder="Scanner, saisir un produit ou *fin EAN" inputMode="search" /></label>
              <label className="wide"><span>Produit analysé</span><input value={product} onChange={(event) => {
                setProduct(event.target.value);
                setSensitivityReason("");
                setSensitivityConfidence("");
              }} placeholder="Nom du produit" /></label>
              <label><span>Prix d’achat HT</span><input type="number" min="0" step="0.01" value={purchasePrice || ""} onChange={(event) => setPurchasePrice(numberValue(event.target.value))} placeholder="0,00" /></label>
              <label><span>Prix de vente actuel TTC</span><input type="number" min="0" step="0.01" value={currentPrice || ""} onChange={(event) => setCurrentPrice(numberValue(event.target.value))} placeholder="0,00" /></label>
              <label><span>TVA</span><input type="number" min="0" max="20" step="0.1" value={vatRate || ""} onChange={(event) => setVatRate(numberValue(event.target.value))} /></label>
            </div>

            <section className="pricing-margin-summary">
              <div><small>TAUX DE MARQUE ACTUEL</small><strong>{margins.current.markRate > 0 ? `${margins.current.markRate.toFixed(1)} %` : "—"}</strong><span>{margins.current.euros > 0 ? `${money(margins.current.euros)} HT par unité` : "Renseignez achat et vente"}</span></div>
              <label><span>SIMULER UN PRIX TTC</span><input type="number" min="0" step="0.01" value={simulatedPrice || ""} onChange={(event) => setSimulatedPrice(numberValue(event.target.value))} placeholder={recommendation.target ? recommendation.target.toFixed(2) : "0,00"} /></label>
              <div className="simulation"><small>TAUX DE MARQUE SIMULÉ</small><strong>{margins.simulation.markRate > 0 ? `${margins.simulation.markRate.toFixed(1)} %` : "—"}</strong><span>{margins.simulation.euros > 0 ? `${money(margins.simulation.euros)} HT par unité` : "Simulation instantanée"}</span></div>
            </section>

            <details className="pricing-market">
              <summary>Ajouter des repères de marché <span>facultatif</span></summary>
              <div>
                <label>Prix bas<input type="number" min="0" step="0.01" value={marketLow || ""} onChange={(event) => setMarketLow(numberValue(event.target.value))} /></label>
                <label>Prix moyen<input type="number" min="0" step="0.01" value={marketAverage || ""} onChange={(event) => setMarketAverage(numberValue(event.target.value))} /></label>
                <label>Prix haut<input type="number" min="0" step="0.01" value={marketHigh || ""} onChange={(event) => setMarketHigh(numberValue(event.target.value))} /></label>
                <label>Prix zone LGPI<input type="number" min="0" step="0.01" value={marketZone || ""} onChange={(event) => setMarketZone(numberValue(event.target.value))} /></label>
              </div>
            </details>

            <div className="pricing-choices">
              <fieldset className="pricing-sensitivity"><legend>Suggestion de sensibilité par l’IA</legend>
                <div className="pricing-sensitivity-head">
                  <span>✦ Notoriété · comparaison des prix · fréquence d’achat · concurrence web · technicité · valeur du conseil</span>
                  <button type="button" className="pricing-sensitivity-run" disabled={sensitivityLoading || !product.trim()} onClick={() => void analyzeSensitivity()}>
                    {sensitivityLoading ? "Analyse web en cours…" : sensitivityReason ? "↻ Réanalyser" : "✨ Analyser avec l’IA"}
                  </button>
                </div>
                <p className={sensitivityLoading ? "loading" : ""}>{sensitivityLoading ? "L’IA recherche les signaux utiles et évalue la comparabilité du produit…" : sensitivityReason || "Choisissez un produit du catalogue ou saisissez son nom : l’IA proposera elle-même la sensibilité à retenir."}</p>
                {sensitivityReason && <div className="pricing-sensitivity-verdict"><b>Suggestion IA</b><strong>{sensitivity === "very_sensitive" ? "Très sensible / comparable" : sensitivity === "low" ? "Peu sensible / conseil" : "Sensible"}</strong><small>Confiance {sensitivityConfidence === "high" ? "élevée" : sensitivityConfidence === "low" ? "prudente" : "moyenne"} · recherche internet incluse lorsque pertinente</small></div>}
                <div className="pricing-sensitivity-override"><small>Corriger la suggestion si votre connaissance du terrain l’exige</small>{([["very_sensitive", "🎯 Très sensible"], ["sensitive", "👀 Sensible"], ["low", "💬 Peu sensible"]] as const).map(([value, label]) => <button type="button" key={value} className={sensitivity === value ? "active" : ""} onClick={() => setSensitivity(value)}>{sensitivity === value ? "✓ " : ""}{label}</button>)}</div>
              </fieldset>
              <fieldset><legend>Positionnement souhaité</legend>
                {([["aggressive", "⚡ Agressif ++"], ["moderate", "⚖️ Modéré"], ["soft", "🌿 Doux"], ["high_margin", "💰 Marge forte"]] as const).map(([value, label]) => <button type="button" key={value} className={positioning === value ? "active" : ""} onClick={() => setPositioning(value)}>{positioning === value ? "✓ " : ""}{label}</button>)}
              </fieldset>
              <fieldset><legend>Zone de chalandise</legend>
                {([["local", "🏘️ Très locale"], ["mixed", "📍 Mixte"], ["destination", "🌊 Passage / destination"]] as const).map(([value, label]) => <button type="button" key={value} className={catchment === value ? "active" : ""} onClick={() => setCatchment(value)}>{catchment === value ? "✓ " : ""}{label}</button>)}
              </fieldset>
            </div>
          </div>

          <aside className="pricing-result">
            <header><span>02</span><div><small>RECOMMANDATION IMMÉDIATE</small><h2>{product || "Votre prix suggéré"}</h2></div></header>
            <div className="pricing-target">
              <small>PRIX CONSEILLÉ TTC</small>
              <strong>{recommendation.target > 0 ? money(recommendation.target) : "—"}</strong>
              <span>Taux de marque estimé {recommendation.grossMargin > 0 ? `${recommendation.grossMargin.toFixed(1)} %` : "à calculer"}</span>
            </div>
            <div className="pricing-range">
              <span><small>Plancher prudent</small><strong>{recommendation.floor > 0 ? money(recommendation.floor) : "—"}</strong></span>
              <span><small>Plafond cohérent</small><strong>{recommendation.ceiling > 0 ? money(recommendation.ceiling) : "—"}</strong></span>
            </div>
            <div className="pricing-explanation">
              <p><i>1</i><span><b>Rentabilité</b>Le prix d’achat et la TVA déterminent un plancher viable.</span></p>
              <p><i>2</i><span><b>Marché local</b>Les repères LGPI et votre zone tempèrent la recommandation.</span></p>
              <p><i>3</i><span><b>Positionnement</b>Le curseur choisi ajuste le taux de marque et l’image prix.</span></p>
            </div>
            <button type="button" className="pricing-ai-button" disabled={aiLoading || recommendation.target <= 0} onClick={() => void askAi()}>
              {aiLoading ? "Analyse IA en cours…" : "✨ Affiner et expliquer avec l’IA"}
            </button>
            <small className={`pricing-ai-status ${aiConfigured ? "ready" : ""}`}>{aiConfigured ? `IA prête · ${aiModel}` : "Moteur local actif · clé OpenAI à configurer pour l’analyse avancée"}</small>
            {aiAnswer && <div className="pricing-ai-answer"><span>ANALYSE IA</span><p>{aiAnswer}</p></div>}
          </aside>
        </section>

        <section className="pricing-batch">
          <div><span>📚 ANALYSE CATALOGUE LGPI</span><h2>Analyser tout un laboratoire</h2><p>Le fabricant de la colonne J de l’import LGPI est utilisé comme laboratoire. Aucun nouveau fichier n’est nécessaire.</p></div>
          <div className="pricing-batch-form">
            <label><span>Fabricant / laboratoire LGPI</span>{catalogSuppliers.length ? <select value={catalogLaboratory} onChange={(event) => setCatalogLaboratory(event.target.value)}><option value="">Choisir dans la base LGPI…</option>{catalogSuppliers.map((item) => <option value={item.supplier} key={item.supplier}>{item.supplier} · {Number(item.product_count).toLocaleString("fr-FR")} produits{Number(item.pharmacy_count ?? 0) > 1 ? ` · ${item.pharmacy_count} pharmacies` : ""}</option>)}</select> : <input value={catalogLaboratory} onChange={(event) => setCatalogLaboratory(event.target.value)} placeholder="Le fabricant apparaîtra après le prochain import LGPI" />}</label>
            <label className="file"><span>Tarif laboratoire (facultatif)</span><input type="file" accept=".pdf,.csv,.xlsx,.xls" onChange={(event) => setCatalogFile(event.target.files?.[0]?.name ?? "")} /></label>
            <label className="pricing-non-reimbursed"><input type="checkbox" checked={catalogPhnOnly} onChange={(event) => setCatalogPhnOnly(event.target.checked)} /><span>Analyser uniquement les produits PHN</span></label>
            <button type="button" disabled={!catalogLaboratory.trim() || catalogLoading} onClick={() => void analyzeCatalog()}>{catalogLoading ? `Analyse IA ${catalogProgress} %` : "Analyser tout le laboratoire avec l’IA"}</button>
            <div className="pricing-batch-scope"><span>Périmètre</span><button type="button" className={catalogScope === "network" ? "active" : ""} onClick={() => setCatalogScope("network")}>Les 3 pharmacies</button><button type="button" className={catalogScope === "pharmacy" ? "active" : ""} onClick={() => setCatalogScope("pharmacy")}>{pharmacy} seulement</button></div>
          </div>
          {catalogFile && <small className="pricing-batch-file">Fichier complémentaire sélectionné : {catalogFile}</small>}
          {catalogAnalysis.length > 0 && <div className="pricing-batch-results">
            <div className="pricing-batch-kpis"><span><b>{catalogAnalysis.length}</b> produits trouvés</span><span><b>{catalogSummary.priced}</b> prix exploitables</span><span><b>{catalogSummary.opportunities}</b> ajustements à étudier</span><span><b>{catalogSummary.averageMark > 0 ? `${catalogSummary.averageMark.toFixed(1)} %` : "—"}</b> taux de marque moyen</span></div>
            <div className="pricing-catalog-tools">
              <label><span>Rechercher un produit</span><input value={catalogSearch} onChange={(event) => setCatalogSearch(event.target.value)} placeholder="Dénomination ou EAN" /></label>
              <label><span>Classer par</span><select value={catalogSort} onChange={(event) => setCatalogSort(event.target.value as typeof catalogSort)}><option value="product">Produit</option><option value="sensitivity">Sensibilité</option><option value="decision">Décision</option><option value="decided_price">Prix décidé</option><option value="mark_rate">Taux de marque</option></select></label>
            </div>
            <div className="pricing-batch-table"><table><thead><tr><th>Produit</th><th>Achat HT</th><th>Prix actuel</th><th>Sensibilité IA</th><th>Objectif d’agressivité</th><th>Prix conseillé</th><th>Prix décidé</th><th>Taux de marque</th><th>Décision</th></tr></thead><tbody>{displayedCatalog.slice(0, 250).map((row) => <tr key={`${row.pharmacy}-${row.ean}`}><td><b>{row.product}</b><small>{row.ean}</small></td><td>{numberValue(row.purchase_price) ? money(numberValue(row.purchase_price)) : "—"}</td><td>{numberValue(row.sale_price) ? money(numberValue(row.sale_price)) : "—"}</td><td><span className={`pricing-ai-sensitivity ${row.aiSensitivity}`} title={row.aiReason}>{row.aiSensitivity === "very_sensitive" ? "Très sensible" : row.aiSensitivity === "low" ? "Peu sensible" : "Sensible"}</span><small>{row.aiReason}</small></td><td><select className="pricing-aggressiveness" value={row.aggressiveness} onChange={(event) => updateCatalogAggressiveness(row, event.target.value as Positioning)}>{aggressivenessOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></td><td><b>{row.suggestedPrice > 0 ? money(row.suggestedPrice) : "—"}</b><small>{row.suggestedMarkRate > 0 ? `${row.suggestedMarkRate.toFixed(1)} % de marque` : ""}</small></td><td><label className="pricing-decided-price"><input inputMode="decimal" value={row.decidedPrice || ""} onChange={(event) => { const value = numberValue(event.target.value); setCatalogAnalysis((current) => current.map((item) => item.pharmacy === row.pharmacy && item.ean === row.ean ? { ...item, decidedPrice: value, decidedMarkRate: catalogMarkRate(value, item.purchase_price, item.vat_rate) } : item)); }} /><span>€</span></label></td><td><b className={row.decidedMarkRate >= 30 ? "pricing-mark-good" : "pricing-mark-watch"}>{row.decidedMarkRate > 0 ? `${row.decidedMarkRate.toFixed(1)} %` : "—"}</b></td><td><span className={row.action === "Prix cohérent" ? "ok" : row.action.includes("étudier") ? "review" : "missing"}>{row.action}</span></td></tr>)}</tbody></table></div>
            {!displayedCatalog.length && <p className="pricing-catalog-empty">Aucun produit ne correspond aux filtres.</p>}
            {displayedCatalog.length > 250 && <small className="pricing-batch-limit">Les 250 premières lignes sont affichées sur {displayedCatalog.length.toLocaleString("fr-FR")} correspondant aux filtres.</small>}
          </div>}
        </section>
        </>}
      </section>

      {notice && <div className="module-toast" role="status" onClick={() => setNotice("")}>{notice}</div>}
    </main>
  );
}
